import { apiClient } from '../api-client';
import type { ApiResponse } from '@/types/api';
import type { Order, OrderDetail, CreateOrderRequest, UpdateOrderRequest, PickEvent, PickOverrideRequest } from '@/types/order';

export const ordersApi = {
  getAll: async (status?: string, sortBy?: string, sortDirection?: string) => {
    const params: Record<string, string> = {};
    if (status) params.status = status;
    if (sortBy) params.sortBy = sortBy;
    if (sortDirection) params.sortDirection = sortDirection;
    const response = await apiClient.get<ApiResponse<Order[]>>('/orders', { params: Object.keys(params).length > 0 ? params : undefined });
    return response.data.data;
  },

  getById: async (id: number) => {
    const response = await apiClient.get<ApiResponse<OrderDetail>>(`/orders/${id}`);
    return response.data.data;
  },

  create: async (data: CreateOrderRequest) => {
    const response = await apiClient.post<ApiResponse<Order>>('/orders', data);
    return response.data.data;
  },

  unlock: async (id: number) => {
    await apiClient.post(`/orders/${id}/unlock`);
  },

  pickOverride: async (id: number, data: PickOverrideRequest) => {
    await apiClient.post(`/orders/${id}/pick-override`, data);
  },

  getPickEvents: async (id: number) => {
    const response = await apiClient.get<ApiResponse<PickEvent[]>>(`/orders/${id}/pick-events`);
    return response.data.data;
  },

  delete: async (id: number) => {
    await apiClient.delete(`/orders/${id}`);
  },

  getDeleted: async () => {
    const response = await apiClient.get<ApiResponse<Order[]>>('/orders/deleted');
    return response.data.data;
  },

  restore: async (id: number) => {
    await apiClient.post(`/orders/${id}/restore`);
  },

  reassignItem: async (orderId: number, orderItemId: number, replacementInventoryItemId: number) => {
    await apiClient.post(`/orders/${orderId}/items/${orderItemId}/reassign`, {
      replacementInventoryItemId,
    });
  },

  addItem: async (orderId: number, inventoryItemId: number) => {
    await apiClient.post(`/orders/${orderId}/items`, { inventoryItemId });
  },

  removeItem: async (orderId: number, orderItemId: number) => {
    await apiClient.delete(`/orders/${orderId}/items/${orderItemId}`);
  },

  reorderItems: async (orderId: number, itemIds: number[]) => {
    await apiClient.put(`/orders/${orderId}/reorder`, {
      itemIds,
    });
  },

  update: async (id: number, data: UpdateOrderRequest) => {
    const response = await apiClient.put<ApiResponse<Order>>(`/orders/${id}`, data);
    return response.data.data;
  },

  hold: async (id: number) => {
    await apiClient.put(`/orders/${id}/hold`);
  },

  resume: async (id: number) => {
    await apiClient.put(`/orders/${id}/resume`);
  },

  updateTimestamps: async (id: number, data: { createdAt?: string; completedAt?: string; claimedAt?: string }) => {
    await apiClient.put(`/orders/${id}/timestamps`, data);
  },

  updateItemTimestamp: async (orderId: number, itemId: number, data: { pickedAt?: string }) => {
    await apiClient.put(`/orders/${orderId}/items/${itemId}/timestamps`, data);
  },
};
